import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import NumaSelect from "@/components/ui/numa-select";
import {
  AlertCircle, ExternalLink, Gift, HelpCircle, KeyRound, Link2, Loader2, ShieldCheck,
} from "lucide-react";
import NumaLogo from "@/components/NumaLogo";
import { llm, normalizeError } from "@/lib/api";
import { beginAuthorisation, callbackUrl } from "@/lib/oauth";
import { useLLM } from "@/contexts/LLMContext";

/**
 * Asked the first time an AI action is used, never at login.
 *
 * The key is validated against the provider before it is accepted, so a typo
 * surfaces here instead of failing later in the middle of someone's work.
 */
export default function ApiKeyDialog() {
  const { dialogOpen, dialogReason, handleDialogChange, onKeyConfigured } = useLLM();
  const [catalogue, setCatalogue] = useState([]);
  const [accountProviders, setAccountProviders] = useState([]);
  const [connecting, setConnecting] = useState("");
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [providerId, setProviderId] = useState("anthropic");
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [store, setStore] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dialogOpen) return;
    setError(dialogReason?.code && dialogReason.code !== "no_key" ? dialogReason : null);
    llm
      .providers()
      .then((data) => {
        setCatalogue(data.providers || []);
        setAccountProviders(data.oauth_providers || []);
        if (data.active) setProviderId(data.active);
      })
      .catch(() => {
        setCatalogue([]);
        setAccountProviders([]);
      });
    setShowKeyForm(false);
  }, [dialogOpen, dialogReason]);

  const provider = useMemo(
    () => catalogue.find((p) => p.id === providerId) || null,
    [catalogue, providerId]
  );

  useEffect(() => {
    setModel(provider?.default_model || "");
    setBaseUrl("");
  }, [provider]);

  const connectAccount = async (provider) => {
    setConnecting(provider.id);
    setError(null);
    try {
      const { challenge } = await beginAuthorisation(provider.id);
      const { url } = await llm.oauthStart(
        provider.id,
        callbackUrl(provider.callback_path),
        challenge
      );
      window.location.assign(url);
    } catch (err) {
      setConnecting("");
      setError(err?.message ? { message: err.message } : normalizeError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await llm.saveKey({
 provider: providerId,
        api_key: apiKey.trim(),
 model: model.trim() || undefined,
        base_url: baseUrl.trim() || undefined,
        store,
      });
      await onKeyConfigured({ ...result, api_key: apiKey.trim(), base_url: baseUrl.trim() });
      setApiKey("");
    } catch (err) {
      setError(normalizeError(err));
    } finally {
      setBusy(false);
    }
  };

  const firstTime = !dialogReason || dialogReason.code === "no_key";

  return (
    <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="api-key-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <NumaLogo size={9} className="numa-mark" variant={0} />
            <div>
              <DialogTitle className="font-serif-display text-xl">
                {firstTime ? "Collega un modello" : "Collegamento non più valido"}
              </DialogTitle>
              <DialogDescription className="numa-meta mt-0.5">
                {firstTime
                  ? "Un minuto, e si può cambiare o togliere quando vuoi."
                  : dialogReason?.message}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div
            role="alert"
            className="flex gap-2.5 rounded-md border border-danger/30 bg-danger/5 px-3 py-2.5 text-[13px]"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
            <div>
              <p className="font-medium text-ink">{error.message}</p>
              {error.action && <p className="mt-0.5 text-ink-muted">{error.action}</p>}
            </div>
          </div>
        )}

        {/*
          Che cos'è questa finestra, detto a chi non lo sa.

          Chi apre NUMA per scrivere una tesi non sa che cos'è una API key, e
          non deve saperlo: deve sapere che l'intelligenza artificiale non è
          dentro NUMA, che la sceglie e la paga lui, e che senza non perde
          l'editor - perde le letture. Tre frasi, prima di qualunque campo da
          riempire.
        */}
        <div className="rounded-md border border-line bg-surface-2 px-3.5 py-3">
          <p className="text-[13.5px] font-medium leading-relaxed text-ink">
            L&apos;intelligenza artificiale non è dentro NUMA: la colleghi tu, e la paghi
            direttamente a chi la fornisce.
          </p>
          <p className="numa-meta mt-1.5 leading-relaxed">
            Scrivere, impaginare, i controlli automatici, la bibliografia e l&apos;esportazione
            funzionano senza. Serve un modello solo per le <span className="text-ink">letture</span>:
            la revisione critica, le riscritture, le domande sui PDF.
          </p>
          <p className="numa-meta mt-1.5 leading-relaxed">
            Si paga a consumo, e solo quando usi quelle funzioni: una rilettura costa pochi
            centesimi. NUMA non prende abbonamenti e non guadagna su quello che spendi.
          </p>
        </div>

        {/* Connecting an account is the path most people can follow: no key to
            find, nothing to paste. The key form stays available below it. */}
        {/* Cost is the first question most people have, so it is answered before
            anything else rather than hidden in a collapsed section. */}
        <div className="rounded-md border border-ok/30 bg-ok/5 p-3.5">
          <p className="flex items-center gap-1.5 text-[14px] font-medium text-ink">
            <Gift className="h-4 w-4 text-ok" /> Si può usare NUMA senza spendere nulla
          </p>
          <ul className="mt-2 space-y-2 text-[12.5px] leading-relaxed text-ink-muted">
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-ok" />
              <span>
                <span className="font-medium text-ink">Google AI Studio</span> — chiave gratuita,
                senza carta di credito, con una quota generosa al giorno.{" "}
                <button
                  type="button"
                  onClick={() => { setProviderId("google"); setShowKeyForm(true); }}
                  className="font-medium text-brand hover:underline"
                >
                  Usa questo
                </button>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-ok" />
              <span>
                <span className="font-medium text-ink">Groq</span> — gratuito, anch&apos;esso
                senza carta.{" "}
                <button
                  type="button"
                  onClick={() => { setProviderId("groq"); setShowKeyForm(true); }}
                  className="font-medium text-brand hover:underline"
                >
                  Usa questo
                </button>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-ok" />
              <span>
                <span className="font-medium text-ink">Un modello sul tuo computer</span> —
                installa <a href="https://ollama.com" target="_blank" rel="noreferrer noopener" className="text-brand hover:underline">Ollama</a>,
                esegui <code className="font-mono text-[11px] text-ink">ollama pull llama3.1</code>,
                e il testo non lascia nemmeno il tuo pc.{" "}
                <button
                  type="button"
                  onClick={() => { setProviderId("custom"); setShowKeyForm(true); }}
                  className="font-medium text-brand hover:underline"
                >
                  Usa questo
                </button>
              </span>
            </li>
          </ul>
          <p className="mt-2.5 border-t border-ok/20 pt-2 text-2xs leading-relaxed text-ink-muted">
            Anche con i provider a pagamento, <span className="font-medium text-ink">la chiave
            è gratuita</span>: si paga solo il testo effettivamente elaborato, e solo quando usi
            una funzione assistita.
          </p>
        </div>

        {accountProviders.length > 0 && (
          <div className="space-y-2.5">
            {accountProviders.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`connect-${p.id}`}
                onClick={() => connectAccount(p)}
                disabled={Boolean(connecting)}
                className="flex w-full items-start gap-3 rounded-md border border-brand bg-brand-soft px-4 py-3.5 text-left transition-colors hover:bg-brand-soft/70 disabled:opacity-60"
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-on-solid">
                  {connecting === p.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Link2 className="h-4 w-4" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-medium text-ink">
                    Connetti il tuo account {p.label}
                  </span>
                  <span className="mt-0.5 block text-[12.5px] leading-relaxed text-ink-muted">
                    {p.blurb}
                  </span>
                </span>
              </button>
            ))}

            {/*
              Il passo a passo, per chi quel nome non l'ha mai sentito.

              Il pulsante qui sopra fa tutto da sé, ma «Connetti il tuo account
              OpenRouter» presuppone di sapere che cosa sia OpenRouter e di
              averne uno. Chi non lo sa si ferma lì.
            */}
            <details className="rounded-md border border-line bg-surface px-3.5 py-2.5">
              <summary className="cursor-pointer text-[13px] font-medium text-ink">
                Non sai cos&apos;è OpenRouter? Ecco cosa succede, passo per passo
              </summary>
              <p className="numa-meta mt-2 leading-relaxed">
                OpenRouter è un intermediario: con un solo account si usano i modelli di
                Anthropic, OpenAI, Google e altri, senza aprire un conto con ciascuno. È il
                modo più corto per arrivare a scrivere.
              </p>
              <ol className="mt-2.5 space-y-1.5 text-[12.5px] leading-relaxed text-ink-muted">
                <li>
                  <span className="font-medium text-ink">1.</span> Premi «Connetti il tuo
                  account OpenRouter» qui sopra: si apre il sito di OpenRouter in un&apos;altra
                  scheda.
                </li>
                <li>
                  <span className="font-medium text-ink">2.</span> Se non hai un account,
                  registrati con Google o con la tua email — meno di un minuto, nessuna carta
                  di credito.
                </li>
                <li>
                  <span className="font-medium text-ink">3.</span> OpenRouter chiede se
                  autorizzi NUMA: accetti, e torni qui da solo. Da quel momento le letture
                  funzionano.
                </li>
                <li>
                  <span className="font-medium text-ink">4.</span> Per usare i modelli migliori
                  serve del credito: su OpenRouter, <span className="text-ink">Settings →
                  Credits</span>, cinque euro bastano per centinaia di riletture. Con i modelli
                  gratuiti non serve nemmeno quello.
                </li>
              </ol>
              <p className="mt-2.5 border-t border-line pt-2 text-2xs leading-relaxed text-ink-muted">
                Il collegamento si toglie quando vuoi, dal menù in basso a sinistra. NUMA non
                vede la tua password di OpenRouter: riceve solo un permesso, revocabile da lì.
              </p>
            </details>

            <button
              type="button"
              data-testid="toggle-key-form"
              onClick={() => setShowKeyForm((v) => !v)}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-line py-2 text-[12.5px] text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
            >
              <KeyRound className="h-3.5 w-3.5" />
              {showKeyForm
                ? "Nascondi l'inserimento manuale"
                : "Ho già una chiave di un altro servizio: la incollo qui"}
            </button>
          </div>
        )}

        <form
          onSubmit={submit}
          className={`space-y-4 ${accountProviders.length > 0 && !showKeyForm ? "hidden" : ""}`}
        >
          <div className="space-y-1.5">
            <Label htmlFor="provider">Provider</Label>
            <NumaSelect
              id="provider"
              testId="provider-select"
              value={providerId}
              onValueChange={setProviderId}
              className="h-10 w-full text-sm"
              options={[...catalogue]
                .sort((a, b) => Number(b.recommended) - Number(a.recommended))
                .map((p) => ({
                  value: p.id,
                  label: p.label,
                  hint: p.no_card
                    ? "gratuito, senza carta"
                    : p.free_tier
                    ? "ha un piano gratuito"
                    : "",
                }))}
            />
            {provider?.notes && <p className="numa-meta">{provider.notes}</p>}
            {provider?.free_tier && (
              <p className="inline-flex items-center gap-1.5 text-[12.5px] text-ok">
                <Gift className="h-3.5 w-3.5" />
                {provider.no_card
                  ? "Piano gratuito, senza carta di credito"
                  : "Ha un piano gratuito"}
              </p>
            )}
          </div>

          {/* Most people have never made an API key. Saying "go to the console"
              is not instructions; these are the actual clicks, per provider. */}
          {provider?.steps?.length > 0 && (
            <details className="rounded-md border border-line bg-surface-2 px-3 py-2.5" open={firstTime}>
              <summary className="flex cursor-pointer items-center gap-1.5 text-[13px] font-medium text-ink">
                <HelpCircle className="h-3.5 w-3.5 text-ink-muted" />
                Non sai cos&apos;è una API key? Ecco come ottenerla
              </summary>
              <p className="mt-2 text-[12.5px] leading-relaxed text-ink-muted">
                Una API key è una password che il provider del modello ti assegna. NUMA la usa
                per parlare con il modello a tuo nome.
              </p>
              <p className="mt-2 rounded-md border border-line bg-surface px-2.5 py-2 text-[12.5px] leading-relaxed text-ink-muted">
                <span className="font-medium text-ink">Creare la chiave non costa nulla.</span>{" "}
                Non è un abbonamento e non si paga per averla: si paga — presso il provider,
                non presso NUMA — solo il testo effettivamente elaborato, quando usi una
                funzione assistita. Se non la usi, non spendi niente. Google e Groq offrono
                una quota gratuita senza nemmeno registrare una carta.
              </p>
              <ol className="mt-2.5 space-y-1.5">
                {provider.steps.map((step, i) => (
                  <li key={i} className="flex gap-2 text-[12.5px] leading-relaxed text-ink-muted">
                    <span className="mt-px inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-surface text-[10px] font-medium tabular-nums text-ink">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              {provider.key_url && (
                <a
                  href={provider.key_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mt-2.5 inline-flex items-center gap-1 text-[12.5px] font-medium text-brand hover:underline"
                >
                  Apri la pagina di {provider.label} <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </details>
          )}

          {provider?.needs_base_url && (
            <div className="space-y-1.5">
              <Label htmlFor="base-url">URL base</Label>
              <Input
                id="base-url"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="http://localhost:11434/v1"
                className="h-10"
                required
              />
            </div>
          )}

          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="api-key">API key</Label>
              {provider?.key_url && (
                <a
                  href={provider.key_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-[12.5px] text-brand hover:underline"
                >
                  Dove la trovo <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            <p className="numa-meta">
              Gratuita da creare: paghi solo il consumo, e solo se usi le funzioni assistite.
            </p>
            <Input
              id="api-key"
              data-testid="api-key-input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={provider?.key_hint ? `${provider.key_hint}…` : "La tua chiave"}
              className="h-10 font-mono text-[13px]"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="model">Modello</Label>
            {provider?.models?.length ? (
              <NumaSelect
                id="model"
                testId="model-select"
                value={model}
                onValueChange={setModel}
                className="h-10 w-full font-mono text-[13px]"
                options={provider.models.map((m) => ({ value: m, label: m }))}
              />
            ) : (
              <Input
                id="model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="nome del modello"
                className="h-10 font-mono text-[13px]"
                required
              />
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-line bg-surface-2 px-3 py-2.5">
            <Checkbox
              id="store-key"
              checked={store}
              onCheckedChange={(v) => setStore(v === true)}
              className="mt-0.5"
            />
            <span className="text-[13px] leading-relaxed">
              <span className="font-medium text-ink">Ricorda questa chiave sul mio account</span>
              <span className="mt-0.5 block text-ink-muted">
                {store
                  ? "Salvata cifrata (AES-256-GCM) e mai restituita in chiaro al browser."
                  : "Resterà solo in questa scheda del browser: dovrai reinserirla al prossimo accesso."}
              </span>
            </span>
          </label>

          <div className="flex items-center justify-between gap-3 pt-1">
            <p className="flex items-center gap-1.5 text-[12px] text-ink-subtle">
              <ShieldCheck className="h-3.5 w-3.5" />
              La chiave viene verificata con una richiesta di prova.
            </p>
            <Button type="submit" disabled={busy || !apiKey.trim()} data-testid="api-key-submit">
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Verifica…
                </>
              ) : (
                <>
                  <KeyRound className="h-4 w-4" /> Collega
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
